import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import ImageExt from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import {
  FileText, Plus, Search, Pencil, Trash2, Eye, Copy, Archive, Upload,
  ChevronDown, Clock, CheckCircle, AlertTriangle, Globe, X, ArrowLeft,
  Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter,
  AlignRight, List, ListOrdered, Quote, Image, Link2, Heading1, Heading2,
  Heading3, Undo2, Redo2, Code, Minus, History, RotateCcw, Monitor,
  Tablet, Smartphone, Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { refreshSiteNavigation } from "@/hooks/useSiteNavigation";

const API = "/api";

const STATUS_COLORS = {
  draft: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  published: "bg-green-500/20 text-green-400 border-green-500/30",
  scheduled: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  unpublished: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  archived: "bg-red-500/20 text-red-400 border-red-500/30",
};

function StatusBadge({ status }) {
  return <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize", STATUS_COLORS[status] || STATUS_COLORS.draft)}>{status}</span>;
}

// ── Tiptap Editor Toolbar ───────────────────────────────────────────

function EditorToolbar({ editor }) {
  if (!editor) return null;
  const btn = (active, onClick, icon, title) => (
    <button type="button" onClick={onClick} title={title}
      className={cn("p-1.5 rounded-lg transition-colors", active ? "bg-blue-600/30 text-blue-400" : "text-slate-500 hover:text-white hover:bg-slate-800")}>
      {icon}
    </button>
  );
  return (
    <div className="flex flex-wrap gap-0.5 p-2 border-b border-slate-800 bg-slate-900/50">
      {btn(false, () => editor.chain().focus().undo().run(), <Undo2 size={14} />, "Undo")}
      {btn(false, () => editor.chain().focus().redo().run(), <Redo2 size={14} />, "Redo")}
      <div className="w-px bg-slate-800 mx-1" />
      {btn(editor.isActive("heading", { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), <Heading1 size={14} />, "Heading 1")}
      {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), <Heading2 size={14} />, "Heading 2")}
      {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), <Heading3 size={14} />, "Heading 3")}
      <div className="w-px bg-slate-800 mx-1" />
      {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), <Bold size={14} />, "Bold")}
      {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), <Italic size={14} />, "Italic")}
      {btn(editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), <UnderlineIcon size={14} />, "Underline")}
      {btn(editor.isActive("code"), () => editor.chain().focus().toggleCode().run(), <Code size={14} />, "Code")}
      <div className="w-px bg-slate-800 mx-1" />
      {btn(editor.isActive({ textAlign: "left" }), () => editor.chain().focus().setTextAlign("left").run(), <AlignLeft size={14} />, "Left")}
      {btn(editor.isActive({ textAlign: "center" }), () => editor.chain().focus().setTextAlign("center").run(), <AlignCenter size={14} />, "Center")}
      {btn(editor.isActive({ textAlign: "right" }), () => editor.chain().focus().setTextAlign("right").run(), <AlignRight size={14} />, "Right")}
      <div className="w-px bg-slate-800 mx-1" />
      {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), <List size={14} />, "Bullet List")}
      {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), <ListOrdered size={14} />, "Ordered List")}
      {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), <Quote size={14} />, "Quote")}
      {btn(false, () => editor.chain().focus().setHorizontalRule().run(), <Minus size={14} />, "Divider")}
      <div className="w-px bg-slate-800 mx-1" />
      {btn(false, () => {
        const url = window.prompt("Enter link URL:");
        if (url) editor.chain().focus().setLink({ href: url }).run();
      }, <Link2 size={14} />, "Link")}
      {btn(false, () => {
        const url = window.prompt("Enter image URL:");
        if (url) editor.chain().focus().setImage({ src: url }).run();
      }, <Image size={14} />, "Image")}
    </div>
  );
}

// ── Rich Text Editor Wrapper ────────────────────────────────────────

function RichEditor({ content, onChange, placeholder = "Start writing..." }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      ImageExt,
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: content || "",
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: {
      attributes: { class: "prose prose-invert max-w-none p-4 min-h-[200px] focus:outline-none text-sm text-slate-200" },
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "");
    }
  }, [content]);

  return (
    <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-950">
      <EditorToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
//  PAGE EDITOR
// ══════════════════════════════════════════════════════════════════════

function PageEditor({ pageId, onClose }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("fr"); // "fr" | "en"
  const [seoOpen, setSeoOpen] = useState(false);
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [previewDevice, setPreviewDevice] = useState(null);
  const [forms, setForms] = useState([]);

  const isNew = !pageId;

  useEffect(() => {
    axios.get(`${API}/admin/forms`, { withCredentials: true })
      .then(res => setForms((res.data?.forms || res.data || []).filter(f => f.published || f.status === "published")))
      .catch(() => setForms([]));
  }, []);

  useEffect(() => {
    if (pageId) {
      axios.get(`${API}/admin/pages/${pageId}`)
        .then(res => { setPage(res.data); setLoading(false); })
        .catch(() => setLoading(false));
    } else {
      setPage({
        title_fr: "", title_en: "", slug: "",
        content_fr: "", content_en: "",
        excerpt_fr: "", excerpt_en: "",
        hero_image: "",
        seo_title_fr: "", seo_title_en: "",
        seo_description_fr: "", seo_description_en: "",
        og_image: "", status: "draft",
        show_in_menu: false,
      });
      setLoading(false);
    }
  }, [pageId]);

  const update = (field, value) => {
    setPage(prev => ({ ...prev, [field]: value }));
  };

  const loadRevisions = () => {
    if (!pageId) return;
    setRevisionsOpen(!revisionsOpen);
    if (!revisionsOpen) {
      axios.get(`${API}/admin/pages/${pageId}/revisions`)
        .then(res => setRevisions(res.data || []));
    }
  };

  const restoreRevision = async (revId) => {
    if (!window.confirm("Restore this version? Current content will be replaced.")) return;
    await axios.post(`${API}/admin/pages/${pageId}/revisions/${revId}/restore`);
    const res = await axios.get(`${API}/admin/pages/${pageId}`);
    setPage(res.data);
    setRevisionsOpen(false);
  };

  const save = async (newStatus) => {
    if (!page) return;
    setSaving(true);
    try {
      const payload = { ...page };
      if (newStatus) payload.status = newStatus;
      if (isNew) {
        const res = await axios.post(`${API}/admin/pages`, payload);
        setPage(res.data);
      } else {
        await axios.put(`${API}/admin/pages/${pageId}`, payload);
      }
      refreshSiteNavigation();
      onClose(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={24} className="animate-spin text-slate-600" /></div>;
  if (!page) return <div className="text-center py-12 text-slate-500">Page not found</div>;

  return (
    <div data-testid="page-editor">
      {/* Editor Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => onClose(false)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white" data-testid="editor-back">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex items-center gap-2">
          {pageId && (
            <button onClick={loadRevisions} className="flex items-center gap-1 px-3 py-1.5 text-xs text-slate-400 hover:text-white bg-slate-800 border border-slate-700 rounded-lg" data-testid="editor-revisions">
              <History size={13} /> Revisions
            </button>
          )}
          <button onClick={() => save()} disabled={saving} className="px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-lg text-xs font-medium hover:bg-slate-700 disabled:opacity-50" data-testid="editor-save-draft">
            {saving ? <Loader2 size={14} className="animate-spin" /> : "Save Draft"}
          </button>
          <button onClick={() => save("published")} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-500 disabled:opacity-50" data-testid="editor-publish">
            Publish
          </button>
        </div>
      </div>

      {/* Revisions Panel */}
      {revisionsOpen && (
        <div className="mb-4 bg-slate-900/50 border border-slate-800 rounded-xl p-4 max-h-48 overflow-y-auto" data-testid="revisions-panel">
          <h4 className="text-xs font-bold text-white mb-2">Revision History</h4>
          {revisions.length === 0 ? <p className="text-[11px] text-slate-500">No revisions yet.</p> : (
            revisions.map(rev => (
              <div key={rev.id} className="flex items-center justify-between py-1.5 border-b border-slate-800/30 last:border-0">
                <div className="text-[11px] text-slate-400">
                  v{rev.version} · {rev.editor} · {new Date(rev.created_at).toLocaleString()}
                </div>
                <button onClick={() => restoreRevision(rev.id)} className="text-[10px] text-blue-400 hover:underline flex items-center gap-1" data-testid={`restore-${rev.id}`}>
                  <RotateCcw size={10} /> Restore
                </button>
              </div>
            ))
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Language Tabs */}
          <div className="flex gap-2" data-testid="lang-tabs">
            <button onClick={() => setTab("fr")} className={cn("px-4 py-2 rounded-lg text-xs font-medium border transition-colors flex items-center gap-1.5",
              tab === "fr" ? "bg-blue-600/20 text-blue-400 border-blue-500/30" : "text-slate-500 border-slate-800 hover:text-white")}>
              <Globe size={12} /> Français
            </button>
            <button onClick={() => setTab("en")} className={cn("px-4 py-2 rounded-lg text-xs font-medium border transition-colors flex items-center gap-1.5",
              tab === "en" ? "bg-blue-600/20 text-blue-400 border-blue-500/30" : "text-slate-500 border-slate-800 hover:text-white")}>
              <Globe size={12} /> English
            </button>
          </div>

          {/* Title */}
          <div>
            <label className="text-[11px] font-medium text-slate-400 mb-1 block">{tab === "fr" ? "Titre (FR)" : "Title (EN)"}</label>
            <input
              data-testid={`page-title-${tab}`}
              value={tab === "fr" ? (page.title_fr || "") : (page.title_en || "")}
              onChange={e => update(tab === "fr" ? "title_fr" : "title_en", e.target.value)}
              className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-white text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 placeholder-slate-600"
              placeholder={tab === "fr" ? "Titre de la page" : "Page title"}
            />
          </div>

          {/* Content */}
          <div>
            <label className="text-[11px] font-medium text-slate-400 mb-1 block">{tab === "fr" ? "Contenu (FR)" : "Content (EN)"}</label>
            <RichEditor
              content={tab === "fr" ? page.content_fr : page.content_en}
              onChange={html => update(tab === "fr" ? "content_fr" : "content_en", html)}
              placeholder={tab === "fr" ? "Commencez à écrire..." : "Start writing..."}
            />
          </div>

          {/* Excerpt */}
          <div>
            <label className="text-[11px] font-medium text-slate-400 mb-1 block">{tab === "fr" ? "Extrait (FR)" : "Excerpt (EN)"}</label>
            <textarea
              data-testid={`page-excerpt-${tab}`}
              value={tab === "fr" ? (page.excerpt_fr || "") : (page.excerpt_en || "")}
              onChange={e => update(tab === "fr" ? "excerpt_fr" : "excerpt_en", e.target.value)}
              className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 placeholder-slate-600 resize-none"
              rows={2} placeholder={tab === "fr" ? "Résumé court" : "Short summary"}
            />
          </div>

          {/* SEO Section */}
          <div>
            <button onClick={() => setSeoOpen(!seoOpen)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="seo-toggle">
              <ChevronDown size={14} className={cn("transition-transform", seoOpen && "rotate-180")} /> SEO Settings
            </button>
            {seoOpen && (
              <div className="mt-3 space-y-3 p-4 bg-slate-900/50 border border-slate-800 rounded-xl">
                <div>
                  <label className="text-[10px] text-slate-500 mb-1 block">SEO Title ({tab.toUpperCase()})</label>
                  <input data-testid={`seo-title-${tab}`}
                    value={tab === "fr" ? (page.seo_title_fr || "") : (page.seo_title_en || "")}
                    onChange={e => update(tab === "fr" ? "seo_title_fr" : "seo_title_en", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 mb-1 block">SEO Description ({tab.toUpperCase()})</label>
                  <textarea data-testid={`seo-desc-${tab}`}
                    value={tab === "fr" ? (page.seo_description_fr || "") : (page.seo_description_en || "")}
                    onChange={e => update(tab === "fr" ? "seo_description_fr" : "seo_description_en", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 mb-1 block">OG Image URL</label>
                  <input data-testid="og-image"
                    value={page.og_image || ""}
                    onChange={e => update("og_image", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Status */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <h4 className="text-xs font-bold text-white mb-3">Page Settings</h4>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-slate-500 mb-1 block">Status</label>
                <StatusBadge status={page.status || "draft"} />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 mb-1 block">Slug</label>
                <input data-testid="page-slug"
                  value={page.slug || ""}
                  onChange={e => update("slug", e.target.value)}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-mono"
                  placeholder="page-slug"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 mb-1 block">Hero Image</label>
                <input data-testid="hero-image"
                  value={page.hero_image || ""}
                  onChange={e => update("hero_image", e.target.value)}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  placeholder="Image URL"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="show-in-menu">
                <input type="checkbox" checked={page.show_in_menu || false}
                  onChange={e => update("show_in_menu", e.target.checked)}
                  className="w-4 h-4 rounded border-slate-700 bg-slate-950 text-blue-600"
                />
                <span className="text-xs text-slate-400">Show in navigation</span>
              </label>
            </div>
          </div>

          {/* Access / Visibility */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <h4 className="text-xs font-bold text-white mb-3">Access</h4>
            <label className="text-[10px] text-slate-500 mb-1 block">Who can view this page</label>
            <select value={page.visibility || "public"} onChange={e => update("visibility", e.target.value)} data-testid="page-visibility"
              className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs">
              {[["public","Public (everyone)"],["guests","Guests only"],["signed_in","Signed-in users"],["free","Free users"],["premium","Premium (any)"],["paid","Paid Premium only"],["complimentary","Complimentary Premium"],["admins","Admins only"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <p className="text-[10px] text-slate-500 mt-1.5">Enforced by the backend — unauthorized visitors get the correct message.</p>
          </div>

          {/* Navigation placement */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <h4 className="text-xs font-bold text-white mb-3">Navigation Placement</h4>
            <div className="space-y-2">
              {[["header","Header"],["left_menu","Left Main Menu"],["footer","Footer"],["account_menu","Account Menu"]].map(([v,l]) => {
                const menus = (page.nav?.menus) || [];
                return (
                  <label key={v} className="flex items-center gap-2 cursor-pointer" data-testid={`nav-menu-${v}`}>
                    <input type="checkbox" checked={menus.includes(v)}
                      onChange={e => { const s = new Set(menus); e.target.checked ? s.add(v) : s.delete(v); update("nav", { ...(page.nav||{}), menus: [...s] }); }}
                      className="w-4 h-4 rounded border-slate-700 bg-slate-950 text-blue-600" />
                    <span className="text-xs text-slate-400">{l}</span>
                  </label>
                );
              })}
              {((page.nav?.menus)||[]).length > 0 && (
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <input value={page.nav?.label_fr || ""} onChange={e => update("nav", { ...(page.nav||{}), label_fr: e.target.value })} data-testid="nav-label-fr" placeholder="Label (FR)" className="px-2 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs" />
                  <input value={page.nav?.label_en || ""} onChange={e => update("nav", { ...(page.nav||{}), label_en: e.target.value })} data-testid="nav-label-en" placeholder="Label (EN)" className="px-2 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs" />
                  <input type="number" value={page.nav?.order ?? 0} onChange={e => update("nav", { ...(page.nav||{}), order: parseInt(e.target.value)||0 })} data-testid="nav-order" placeholder="Order" className="px-2 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs" />
                  <label className="flex items-center gap-2 text-xs text-slate-400"><input type="checkbox" checked={!!page.nav?.new_tab} onChange={e => update("nav", { ...(page.nav||{}), new_tab: e.target.checked })} className="rounded border-slate-700" /> New tab</label>
                </div>
              )}
            </div>
          </div>

          {/* Custom HTML (Super Admin) */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <h4 className="text-xs font-bold text-white mb-2">Custom HTML Block</h4>
            <p className="text-[10px] text-amber-400 mb-2">Sanitized & rendered in a sandboxed iframe. Super Admin only.</p>
            {["fr","en"].map(l => (
              <textarea key={l} rows={3} value={page[`custom_html_${l}`] || ""} onChange={e => update(`custom_html_${l}`, e.target.value)} data-testid={`custom-html-${l}`}
                placeholder={`Custom HTML (${l.toUpperCase()})`} className="w-full mb-2 px-2 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs font-mono" />
            ))}
          </div>

          {/* Embed a Form */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <h4 className="text-xs font-bold text-white mb-1">Embed a Form</h4>
            <p className="text-[10px] text-slate-500 mb-2">Insert a published form (e.g. Contact) directly into this page. It renders below the content.</p>
            <select value={page.embedded_form_key || ""} onChange={e => update("embedded_form_key", e.target.value)} data-testid="page-embed-form"
              className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs">
              <option value="">— No form —</option>
              {forms.map(f => (
                <option key={f.key} value={f.key}>{f.name_en || f.published?.title_en || f.key}</option>
              ))}
            </select>
            {page.embedded_form_key && <p className="text-[10px] text-green-400 mt-1.5">Form "{page.embedded_form_key}" will appear on this page.</p>}
          </div>

          {/* Quick Actions */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <h4 className="text-xs font-bold text-white mb-3">Actions</h4>
            <div className="space-y-2">
              {page.status !== "published" && (
                <button onClick={() => save("published")} className="w-full px-3 py-2 bg-green-600/20 text-green-400 border border-green-500/30 rounded-lg text-xs font-medium hover:bg-green-600/30 transition-colors" data-testid="action-publish">
                  <CheckCircle size={12} className="inline mr-1" /> Publish
                </button>
              )}
              {page.status === "published" && (
                <button onClick={() => save("unpublished")} className="w-full px-3 py-2 bg-amber-600/20 text-amber-400 border border-amber-500/30 rounded-lg text-xs font-medium hover:bg-amber-600/30 transition-colors" data-testid="action-unpublish">
                  Unpublish
                </button>
              )}
              {page.status !== "archived" && (
                <button onClick={() => save("archived")} className="w-full px-3 py-2 bg-red-600/20 text-red-400 border border-red-500/30 rounded-lg text-xs font-medium hover:bg-red-600/30 transition-colors" data-testid="action-archive">
                  <Archive size={12} className="inline mr-1" /> Archive
                </button>
              )}
              {page.status === "archived" && (
                <button onClick={() => save("draft")} className="w-full px-3 py-2 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-lg text-xs font-medium hover:bg-blue-600/30 transition-colors" data-testid="action-restore">
                  <RotateCcw size={12} className="inline mr-1" /> Restore
                </button>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <h4 className="text-xs font-bold text-white mb-3">Preview</h4>
            <div className="flex gap-2">
              {[{ key: "desktop", icon: Monitor, label: "Desktop" }, { key: "tablet", icon: Tablet, label: "Tablet" }, { key: "mobile", icon: Smartphone, label: "Mobile" }].map(d => (
                <button key={d.key} onClick={() => setPreviewDevice(previewDevice === d.key ? null : d.key)}
                  className={cn("flex-1 py-2 rounded-lg text-[10px] font-medium border transition-colors flex flex-col items-center gap-1",
                    previewDevice === d.key ? "bg-blue-600/20 text-blue-400 border-blue-500/30" : "text-slate-500 border-slate-800 hover:text-white")}
                  data-testid={`preview-${d.key}`}>
                  <d.icon size={14} />{d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preview Iframe */}
          {previewDevice && (
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-amber-400 font-medium">Draft Preview — Not Public</span>
                <button onClick={() => setPreviewDevice(null)} className="text-slate-500 hover:text-white"><X size={12} /></button>
              </div>
              <div className={cn("mx-auto border border-slate-700 rounded-lg overflow-hidden bg-white",
                previewDevice === "desktop" && "w-full h-[300px]",
                previewDevice === "tablet" && "w-[300px] h-[400px]",
                previewDevice === "mobile" && "w-[200px] h-[350px]")}>
                <div className="p-3 overflow-auto h-full text-black text-xs">
                  <h1 className="text-lg font-bold mb-2">{tab === "fr" ? page.title_fr : page.title_en}</h1>
                  <div dangerouslySetInnerHTML={{ __html: tab === "fr" ? page.content_fr : page.content_en }} />
                </div>
              </div>
            </div>
          )}

          {/* Page Info */}
          {page.created_at && (
            <div className="text-[10px] text-slate-600 space-y-0.5">
              <p>Created: {new Date(page.created_at).toLocaleString()}</p>
              <p>Updated: {new Date(page.updated_at).toLocaleString()}</p>
              {page.author && <p>Author: {page.author}</p>}
              {page.version && <p>Version: {page.version}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
//  PAGES LIST
// ══════════════════════════════════════════════════════════════════════

export default function AdminPages() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editingPage, setEditingPage] = useState(undefined); // undefined=list, null=new, string=editing

  const fetchPages = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);
    axios.get(`${API}/admin/pages?${params}`)
      .then(res => { setPages(res.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const deletePage = async (page) => {
    if (!window.confirm(`Archive "${page.title_en || page.title_fr}"?`)) return;
    try {
      await axios.delete(`${API}/admin/pages/${page.id}`);
      fetchPages();
    } catch { /* silent */ }
  };

  const duplicatePage = async (page) => {
    try {
      await axios.post(`${API}/admin/pages/${page.id}/duplicate`);
      fetchPages();
    } catch { /* silent */ }
  };

  const publishPage = async (page, action) => {
    try {
      await axios.post(`${API}/admin/pages/${page.id}/publish`, { action });
      refreshSiteNavigation();
      fetchPages();
    } catch { /* silent */ }
  };

  // If editing a page, show editor
  if (editingPage !== undefined) {
    return (
      <PageEditor
        pageId={editingPage}
        onClose={(saved) => { setEditingPage(undefined); if (saved) fetchPages(); }}
      />
    );
  }

  return (
    <div data-testid="admin-pages">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <FileText size={24} className="text-blue-400" />
          <h2 className="text-2xl font-bold text-white">Pages</h2>
          <span className="text-xs text-slate-500 ml-1">{pages.length} pages</span>
        </div>
        <button onClick={() => setEditingPage(null)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-500 transition-colors" data-testid="create-page-btn">
          <Plus size={16} /> New Page
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <input type="text" placeholder="Search pages..." data-testid="pages-search"
            className="w-full pl-9 pr-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white text-xs placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-1">
          {["", "draft", "published", "scheduled", "unpublished", "archived"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={cn("px-3 py-2 rounded-lg text-[10px] font-medium border transition-colors capitalize",
                statusFilter === s ? "bg-blue-600/20 text-blue-400 border-blue-500/30" : "text-slate-500 border-slate-800 hover:text-white")}
              data-testid={`pages-filter-${s || 'all'}`}>
              {s || "All"}
            </button>
          ))}
        </div>
      </div>

      {/* Pages Table */}
      {loading ? (
        <div className="space-y-2" data-testid="pages-loading">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-14 bg-slate-800/30 rounded-xl animate-pulse border border-slate-800/20" />)}
        </div>
      ) : pages.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/50 border border-dashed border-slate-800 rounded-2xl" data-testid="pages-empty">
          <FileText size={40} className="mx-auto text-slate-700 mb-3" />
          <h3 className="text-lg font-medium text-white mb-1">No pages found</h3>
          <p className="text-sm text-slate-500">Create your first page to get started.</p>
          <button onClick={() => setEditingPage(null)} className="mt-4 px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-500" data-testid="create-first-page">
            <Plus size={14} className="inline mr-1" /> Create Page
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="pages-table">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left text-[11px] text-slate-500 font-medium pb-3 pr-3">Title</th>
                <th className="text-left text-[11px] text-slate-500 font-medium pb-3 pr-3">Slug</th>
                <th className="text-left text-[11px] text-slate-500 font-medium pb-3 pr-3">Status</th>
                <th className="text-left text-[11px] text-slate-500 font-medium pb-3 pr-3">FR</th>
                <th className="text-left text-[11px] text-slate-500 font-medium pb-3 pr-3">EN</th>
                <th className="text-left text-[11px] text-slate-500 font-medium pb-3 pr-3">Author</th>
                <th className="text-left text-[11px] text-slate-500 font-medium pb-3 pr-3">Updated</th>
                <th className="text-right text-[11px] text-slate-500 font-medium pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map(page => (
                <tr key={page.id} className="border-b border-slate-800/30 hover:bg-slate-800/20 transition-colors">
                  <td className="py-3 pr-3">
                    <button onClick={() => setEditingPage(page.id)} className="text-white text-sm font-medium hover:text-blue-400 transition-colors text-left" data-testid={`page-edit-${page.id}`}>
                      {page.title_en || page.title_fr || "Untitled"}
                    </button>
                  </td>
                  <td className="py-3 pr-3 text-xs text-slate-500 font-mono">/{page.slug}</td>
                  <td className="py-3 pr-3"><StatusBadge status={page.status} /></td>
                  <td className="py-3 pr-3">{page.content_fr ? <CheckCircle size={14} className="text-green-400" /> : <X size={14} className="text-slate-700" />}</td>
                  <td className="py-3 pr-3">{page.content_en ? <CheckCircle size={14} className="text-green-400" /> : <X size={14} className="text-slate-700" />}</td>
                  <td className="py-3 pr-3 text-xs text-slate-500">{page.author || "—"}</td>
                  <td className="py-3 pr-3 text-xs text-slate-500">{page.updated_at ? new Date(page.updated_at).toLocaleDateString() : "—"}</td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditingPage(page.id)} title="Edit" className="p-1.5 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-testid={`page-btn-edit-${page.id}`}>
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => duplicatePage(page)} title="Duplicate" className="p-1.5 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
                        <Copy size={13} />
                      </button>
                      {page.status !== "published" && (
                        <button onClick={() => publishPage(page, "publish")} title="Publish" className="p-1.5 text-green-400/50 hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors">
                          <CheckCircle size={13} />
                        </button>
                      )}
                      <button onClick={() => deletePage(page)} title="Archive" className="p-1.5 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                        <Archive size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
